import type { InventoryItemSort } from '@qkindex/contracts';
import { useCallback, useSyncExternalStore } from 'react';
import { getCollectionDataStore } from './collectionData';
import { InventoryResource } from './collectionInventoryStore';

/** Stable subscriptions share page ownership across browse and picker consumers. */
export function useSharedCollectionInventory(
  userId: string,
  collectionIds: readonly string[],
  resources: readonly InventoryResource[],
  itemSort?: InventoryItemSort,
) {
  const store = getCollectionDataStore(userId).inventory;
  const idsKey = JSON.stringify([...new Set(collectionIds)].sort());
  const resourcesKey = JSON.stringify(resources);
  const subscribe = useCallback(
    (listener: () => void) => {
      const ids: string[] = JSON.parse(idsKey);
      const kinds: InventoryResource[] = JSON.parse(resourcesKey);
      const stops = ids.map((id) =>
        store.subscribe(id, kinds, listener, itemSort),
      );
      return () => stops.forEach((stop) => stop());
    },
    [store, idsKey, resourcesKey, itemSort],
  );
  const revision = useSyncExternalStore(subscribe, store.getRevision);
  const loadMore = useCallback(async () => {
    const ids: string[] = JSON.parse(idsKey);
    await Promise.all(
      ids.map((id) => store.loadMore(id, JSON.parse(resourcesKey))),
    );
  }, [store, idsKey, resourcesKey]);
  const refresh = useCallback(() => {
    const ids: string[] = JSON.parse(idsKey);
    ids.forEach((id) => store.reconcile(id, JSON.parse(resourcesKey)));
  }, [store, idsKey, resourcesKey]);
  const retry = useCallback(() => {
    const ids: string[] = JSON.parse(idsKey);
    ids.forEach((id) => store.retry(id));
  }, [store, idsKey]);
  const ids: string[] = JSON.parse(idsKey);
  return {
    revision,
    entries: ids.map((id) => ({ id, ...store.getSnapshot(id) })),
    loadMore,
    refresh,
    retry,
  };
}
